import { useState, useCallback, useEffect } from "react";
import axiosInstance from "../utils/axiosConfig";
import Grid from "@material-ui/core/Grid";
import Button from "../Input/Button";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import BrAvatar from "../Profile/Avatar";


export default function SelectChildUser(props) {
    const { kids, proceedWithChild, user } = props;
    const useStyles = makeStyles((theme) => ({
        button: {
            width: "120px",
            height: "25px",
            borderRadius: "12px",
            fontSize: "10px",
            margin: "5px"
        },
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            overflow: "auto",
            paddingTop: "8vh", // Move content up by starting closer to top
            paddingBottom: "100px" // Account for footer height
        },
        bullet: {
            minHeight: "100px",
            margin: "0 2px",
            transform: "scale(0.8)"
        },
        title: {
            fontSize: 32,
            fontFamily: "GoodUnicorn",
            color: "#bbb"
        },
        margin: {
            margin: theme.spacing(1)
        },
        modal: {
            width: "30%", 
            minWidth: "300px",
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            position: "relative",
            zIndex: 10,
            marginTop: "20px"
        },
        divider: {
            margin: 10,
        },
        selectedAvatar: {
            border: "3px solid #bc40fb",
            borderRadius: "50%"
        },
        deselectedAvatar: {
            border: "3px solid #FFF"
        },
        footer: {
            backgroundColor: '#b4fa64',
            width: '100%',
            padding: theme.spacing(2),
            marginTop: 'auto',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 999
        },
        footerLink: {
            margin: "0 15px",
            color: "#333",
            textDecoration: "none",
            fontWeight: "bold",
            "&:hover": {
                textDecoration: "underline"
            }
        },
        proceedButton: {
            minWidth: "120px",
            padding: "10px 20px",
            backgroundColor: "#E040FB",
            color: "white",
            "&:hover": {
                backgroundColor: "#bc40fb"
            },
            "&:disabled": {
                backgroundColor: "#e0e0e0"
            }
        },
        childContainer: {
            cursor: "pointer",
            padding: "10px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
                backgroundColor: "rgba(224, 64, 251, 0.05)"
            }
        },
        childSelected: {
            backgroundColor: "#ff6419",
        },
        childName: {
            marginTop: "8px",
            fontSize: "1rem",
            fontWeight: "500"
        },
        loadingText: {
            marginTop: "5px",
            fontSize: "0.8rem",
            color: "#666"
        },
        modalTitle: {
            textAlign: "center",
            fontSize: "1.5rem", 
            fontWeight: "bold",
            marginBottom: "20px",
            color: "#555"
        },
        avatarContainer: {
            display: "flex",
            justifyContent: "center",
            marginBottom: "10px"
        },
        logoContainer: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "30px",
            padding: "20px 0"
        },
        logoImage: {
            width: "auto",
            maxWidth: "620px",
            height: "auto",
            objectFit: "contain"
        },
        selectionContainer: {
            marginTop: "10px", // Reduced top margin
            marginBottom: "70px", // Reduced bottom margin for footer
            width: "100%",
            display: "flex",
            justifyContent: "center"
        },
        switchModeTitle: {
            fontSize: '1.2rem',
            color: '#555',
            marginBottom: '10px',
            textAlign: 'center'
        }
    }));
    
    const classes = useStyles();
    const history = useHistory();
    
    // Check if this is a switch action (rather than initial selection)
    const isSwitchingChild = history.location.pathname === '/select-kid';
    
    const [selectedChild, setSelectedChild] = useState(kids?.[0] || {});
    const [isLoading, setIsLoading] = useState(false);

    // Generate child-specific theme based on gender
    const getChildTheme = (child) => {
        return child.gender === "M"
            ? {
                primaryColor: "#3344cc",
                secondaryColor: "#ababdc",
                barColor: "hsl(29deg 91% 79%)",
                backgroundLogo: "/images/m-racing.jpg"
            }
            : {
                primaryColor: "#eb4c8a",
                secondaryColor: "#fccfdf",
                barColor: "#fcedf3",
                backgroundLogo: "/images/f-meadow.jpg"
            };
    };
    
    // Function to fetch and store child settings
    const fetchChildSettings = useCallback(async (childId) => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/user_settings/${childId}`);
            
            window.localStorage.setItem('SELECTED_CHILD_SETTINGS', JSON.stringify(response.data));
            return response.data;
        } catch (error) {
            console.error('Failed to fetch child settings:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);
    
    // Function to fetch complete child user data including fresh avatar
    const fetchChildUserData = useCallback(async (childId) => {
        try {
            const response = await axiosInstance.get(`/user/${childId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch child user data:', error);
            return null;
        }
    }, []);
    
    // Initialize default child selection and settings fetch
    useEffect(() => {
        const initializeDefaultChild = async () => {
            if (kids?.length > 0) {
                const defaultChild = kids[0];
                setSelectedChild(defaultChild);
                
                // Only set the default child ID if we're not switching 
                // (to avoid overriding the current selection when just viewing the component)
                if (!isSwitchingChild) {
                    window.localStorage.setItem("SELECTED_CHILD_ID", defaultChild.id);
                    await fetchChildSettings(defaultChild.id);
                }
            }
        };
        
        initializeDefaultChild();
    }, [kids, fetchChildSettings, isSwitchingChild]);
    
    // Handler for child selection
    const handleChildSelection = async (child) => {
        setSelectedChild(child);
        window.localStorage.setItem("SELECTED_CHILD_ID", child.id);
        await fetchChildSettings(child.id);
    };
    
    // Handler for proceed button
    const handleProceed = async () => {
        if (!selectedChild || !selectedChild.id) {
            console.error('No child selected');
            alert('No child selected');
            return;
        }
        
        setIsLoading(true);
        try {
            // Fetch fresh child user data (including updated avatar) and settings in parallel
            const [freshChildData, settings] = await Promise.all([
                fetchChildUserData(selectedChild.id),
                fetchChildSettings(selectedChild.id)
            ]);
            
            if (freshChildData) {
                // Use fresh child data with updated avatar, merge with settings
                proceedWithChild({
                    ...freshChildData,  // Fresh data includes updated avatar
                    settings: settings || freshChildData.settings
                });
                
                // Always navigate to the parent portal when a child is selected,
                // whether it's the initial selection or switching between children
                history.push("/dashboard/parents");
            } else {
                console.error('Failed to fetch child user data before proceeding');
                alert('Failed to load child data. Please try again.');
            }
        } catch (error) {
            console.error('Error in handleProceed:', error);
            alert('Failed to load child data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Calculate grid size based on number of children
    const getGridSize = () => {
        if (!kids?.length) return 12;
        // For 1-3 children, give each child 4 columns (3 per row)
        // For 4+ children, give each child 3 columns (4 per row)
        return kids.length <= 3 ? 4 : 3;
    };

    return (
        <div className={classes.root}>
            <div className={classes.logoContainer}>
                <img 
                    src="/Login Page Banner_620x225_02.png" 
                    alt="BridgePal Logo" 
                    className={classes.logoImage}
                />
            </div>
            <Card className={classes.modal}>
                <div className={classes.modalTitle}>
                    {isSwitchingChild ? 'Switch Child Profile' : 'Select Child Profile'}
                </div>
                {isSwitchingChild && (
                    <div className={classes.switchModeTitle}>
                        Currently viewing: {user.settings?.display_name || user.name}
                    </div>
                )}
                <Grid 
                    container 
                    spacing={3} 
                    justifyContent="center"
                >
                    {kids?.map((child) => {
                        // Generate theme based on child's gender
                        const childTheme = getChildTheme(child);
                        
                        return (
                            <Grid
                                key={`child-${child.id}`}
                                item
                                xs={getGridSize()}
                                style={{
                                    textAlign: "center"
                                }}
                            >
                                <div 
                                    onClick={() => handleChildSelection(child)}
                                    className={`${classes.childContainer} ${selectedChild.id === child.id ? classes.childSelected : ''}`}
                                >
                                    <div className={classes.avatarContainer}>
                                        <BrAvatar
                                            user={child} // Pass the child as the user prop
                                            theme={childTheme} // Use child-specific theme
                                            avatarStyles={{
                                                cursor: "pointer",
                                                width: "160px",
                                                height: "160px",
                                                margin: "auto"
                                            }}
                                            goToProfile={false}
                                        />
                                    </div>
                                    <div className={classes.childName}>
                                        {child.name}
                                    </div>
                                    {isLoading && child.id === selectedChild.id && (
                                        <div></div>
                                    )}
                                </div>
                            </Grid>
                        );
                    })}

                    <Grid 
                        item 
                        xs={12} 
                        style={{ 
                            textAlign: "center",
                            marginTop: "30px"
                        }}
                    >
                        <Button
                            theme={{
                                secondaryColor: "#bc40fb",
                                primaryColor: "#E040FB",
                            }}
                            onClick={handleProceed}
                            disabled={isLoading}
                            className={classes.proceedButton}
                        >
                            {/* {isLoading ? " " : (isSwitchingChild ? "SWITCH TO THIS CHILD" : "PROCEED")} */}
                            PROCEED
                        </Button>
                    </Grid>
                </Grid>
            </Card>
            <footer className={classes.footer} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999 }}>
                <a href="https://xcbrgpal.com" className={classes.footerLink}>BridgePal Website</a>
                <a href="https://bridgepalkids.com/terms" className={classes.footerLink}>BridgePal Terms of Use</a>
                <a href="https://bridgepalkids.com/privacy-policy" className={classes.footerLink}>BridgePal Privacy Policy</a>
                <a href="https://bridgepalkids.com/kids-privacy-policy" className={classes.footerLink}>BridgePal Kids Privacy</a>
            </footer>
        </div>
    );
}